import { BadgeVariant } from '../../../../components/Badge';
import { StepStatus } from '../StatusStepIndicator';

/**
 * Fingerprint / eApp sub-status → tracker display mapping (ACP-1040).
 *
 * The contractor's vetting has two parallel "regions" — fingerprinting and the
 * electronic application (eApp) — each progressing through the same set of
 * statuses. This module turns a region's status into the pieces the existing
 * ApplicationStatusTracker needs (step status, badge, title, body copy).
 *
 * RegionStatus mirrors APPLICATION_REGION_STATUS in @access-cms/db-core. The UI
 * does not depend on db-core, so the union is redeclared here; ACP-1041 will
 * source this type from @access-cms/api-client once the API response surfaces
 * application.applicationSubStatus.
 */
export type RegionStatus = 'pending' | 'requested' | 'completed' | 'not_required' | 'timeout';

export type SubStatusRegion = 'fingerprints' | 'eapp';

/** Shape of application.applicationSubStatus (see db-core ApplicationRegions). */
export interface SubStatusRegions {
  fingerprints?: RegionStatus;
  eapp?: RegionStatus;
  version?: 1;
}

export interface RegionDisplay {
  /** Tracker step title (the region's human name). */
  title: string;
  /** Drives the step icon (completed / current / error / pending). */
  stepStatus: StepStatus;
  /**
   * Badge to show, or undefined for no badge. `timeout` uses the red
   * "Action Needed" badge + error icon, per the finalized eNOVA tracker design
   * (master doc / Figma). Final copy is pending design.
   */
  badgeVariant?: BadgeVariant;
  /** Accordion body copy. Proposed defaults — pending design confirmation. */
  body: string;
}

// Titles match the eNOVA onboarding Confluence doc / Figma spec (section 3),
// minus the parenthetical "(VAS – OSFLO)" qualifier.
const REGION_TITLE: Record<SubStatusRegion, string> = {
  eapp: 'eApp Submission',
  fingerprints: 'Fingerprint Submission',
};

/**
 * Map a single region's status to its tracker display. Defensive: an undefined
 * status, or any value outside the known set, falls back to a neutral pending
 * "not yet available" state rather than throwing — the local union cannot
 * guarantee what a future API actually sends.
 */
export function mapRegionStatus(region: SubStatusRegion, status: RegionStatus | undefined): RegionDisplay {
  const title = REGION_TITLE[region];
  const isFp = region === 'fingerprints';

  switch (status) {
    case 'completed':
      return {
        badgeVariant: 'complete',
        body: isFp ? 'Your fingerprinting has been completed.' : 'Your eApp has been completed.',
        stepStatus: 'completed',
        title,
      };
    case 'requested':
      return {
        badgeVariant: 'current',
        body: isFp
          ? 'Fingerprinting has been requested. Please complete your fingerprinting appointment.'
          : 'Your eApp has been requested. Please complete your electronic application.',
        stepStatus: 'current',
        title,
      };
    case 'pending':
      return {
        badgeVariant: undefined,
        body: isFp ? 'Fingerprinting has not started yet.' : 'Your eApp has not started yet.',
        stepStatus: 'pending',
        title,
      };
    case 'not_required':
      return {
        badgeVariant: 'complete',
        body: isFp
          ? 'Fingerprinting is not required for your application.'
          : 'An eApp is not required for your application.',
        stepStatus: 'completed',
        title,
      };
    case 'timeout':
      return {
        badgeVariant: 'action-needed',
        body: isFp
          ? 'Your fingerprinting request has timed out. Please review your application and resubmit.'
          : 'Your eApp request has timed out. Please review your application and resubmit.',
        stepStatus: 'error',
        title,
      };
    default:
      // undefined / unknown → neutral "not yet available" (AC#3 per region).
      return { badgeVariant: undefined, body: 'Status not yet available.', stepStatus: 'pending', title };
  }
}
